import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

const ALIGN_CLASS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

export interface ResultScreenProps {
  /** 제목 — 굵은 큰 텍스트(text-xl/semibold). 멀티라인은 `\n` 또는 ReactNode. */
  title: ReactNode;
  /** 보조 설명(선택) — 제목 아래 muted 작은 텍스트. 에러 화면의 긴 안내문 등. */
  description?: ReactNode;
  /** 중간 일러스트(선택) — 페이지가 주입(성공/실패 이미지 등). 있으면 표시. */
  image?: ReactNode;
  /** 이미지 없을 때 fallback 아이콘(lucide). image 가 우선. */
  icon?: LucideIcon;
  /** 하단 버튼 그룹(선택) — full-width 세로 스택. 페이지가 버튼들을 children 으로 합성. */
  actions?: ReactNode;
  /** 제목·설명 텍스트 정렬 — 기본 'left'(현행). result/성공·실패 화면 등은 'center'. */
  align?: keyof typeof ALIGN_CLASS;
}

/**
 * 결과/상태 전체 화면 표준 표현 — 성공·실패·에러(404/503/401) 공통. 제목(큰 굵은 텍스트) +
 * 중간 일러스트(옵션, 없으면 fallback 아이콘) + 하단 버튼 그룹(옵션) 3-슬롯.
 * chrome(header·footer·shell)은 호스트 레이아웃(StandaloneLayout)이 담당, 본 컴포넌트는 내용만.
 * 국소 데이터 로드 실패는 `ErrorState`(heritage `Empty`)가 따로 담당 — 그쪽은 영역 채움 + 재시도.
 */
export function ResultScreen({
  title,
  description,
  image,
  icon: Icon,
  actions,
  align = 'left',
}: ResultScreenProps) {
  return (
    <div className="flex w-full flex-col gap-12">
      <div className={`flex flex-col gap-2 ${ALIGN_CLASS[align]}`}>
        <div className="whitespace-pre-line text-xl font-semibold text-foreground">{title}</div>
        {description ? <p className="text-sm text-foreground">{description}</p> : null}
      </div>

      {image || Icon ? (
        <div className="flex w-full justify-center">
          {image ??
            (Icon ? <Icon className="size-12 text-muted-foreground" aria-hidden="true" /> : null)}
        </div>
      ) : null}

      {actions ? <div className="flex w-full flex-col gap-2">{actions}</div> : null}
    </div>
  );
}
